<script lang="ts">
export default {
  name: 'FilterSlider',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { IsNullOrUndefined } from '@/utilities/Helpers';
import FilterButton from '@/components/FilterButton.vue';
import NumberInput from '@/components/NumberInput.vue';
import { filters } from '@/utilities/Filters';

// -------
// props
// -------

const props = withDefaults(defineProps<{
  values: Array<number>;
  valueFormatter?: string;
  valueFormatterFn?: (value: number | undefined | null) => string | number;
  modelValue?: Array<number>;
  title: string;
  minTitle?: string;
  maxTitle?: string;
  description?: string;
  staticMin?: number;
  staticMax?: number;
  step?: number;
  prefix?: string;
  dataTestPrefix?: string;
  hideSlider?: boolean;
  numericValues?: boolean;
}>(), {
  valueFormatter: 'compactNumber',
  minTitle: 'Minimum',
  maxTitle: 'Maximum',
  step: 1,
  prefix: '',
  dataTestPrefix: 'default',
  hideSlider: false,
  numericValues: false,
  });

const emit = defineEmits<{
  'update:modelValue': [number[]];
}>();

// ----------------
// refs and consts
// ----------------

const dirtyMin = ref<number | undefined>(0);
const dirtyMax = ref<number | undefined>(undefined);
const isDirty = ref(false);
const useSlider = ref(false);
const minErrors = ref<string[]>([]);
const maxErrors = ref<string[]>([]);
const filterApplied = ref(false);


// ---------
// computed
// ---------

const formattedMin = computed(() => {
  if (props.valueFormatterFn) return props.valueFormatterFn(dirtyMin.value).toString()
  return formatRangeValue(dirtyMin.value, props.numericValues);
});

const formattedMax = computed(() => {
  if (props.valueFormatterFn) return props.valueFormatterFn((dirtyMax.value)).toString()
  return formatRangeValue(dirtyMax.value, props.numericValues);
});

const selectedMin = computed(() => {
  return dirtyMin.value ? dirtyMin.value  : undefined;
});

const selectedMax = computed(() => {
  return dirtyMax.value  ? dirtyMax.value  : undefined;
});

const minValue = computed(() => {
  if (!IsNullOrUndefined(props.staticMin)) {
    return props.staticMin;
  }
  let lowest = props.values[0] || 0;
  props.values.forEach((x) => {
    if (x < lowest) {
      lowest = x;
    }
  });
  return lowest;
});

const maxValue = computed(() => {
  if (!IsNullOrUndefined(props.staticMax)) {
    return props.staticMax;
  }
  let lowest = props.values[0] || 0;
  props.values.forEach((x) => {
    if (x > lowest) {
      lowest = x;
    }
  });
  return lowest;
});

const buttonTitle = computed(() => {
  if (!filterApplied.value) {
    return props.title;
  }

  const range = [format(selectedMin.value || 0)];
  if ((selectedMax.value || 0) > 0) {
    range.push(format(selectedMax.value));
  }
  return range.join(' - ');
});

// ----------
// functions
// ----------

function format(val: number | undefined): string | number {
  if (props.valueFormatterFn) {
    return props.valueFormatterFn((val))
  } else {
    return filters.compactNumber(val);
  }
}

function setRange(range: [number, number]): void {
  if (!range[0] && !range[1]) {
    return;
  }
  dirtyMin.value = range[0];
  dirtyMax.value = range[1];
  isDirty.value = true;
  minErrors.value = [];
  maxErrors.value = [];
}

function applyNewRange(): void {
  filterApplied.value = true;
  if (typeof dirtyMin.value !== 'undefined' && typeof dirtyMax.value !== 'undefined') {
    emit('update:modelValue', [dirtyMin.value, dirtyMax.value].filter((x) => !IsNullOrUndefined(x)))
  }
}

function setDirtyMin(value: number): void {
  isDirty.value = true;
  dirtyMin.value = value;
  minErrors.value = (dirtyMax.value && value > dirtyMax.value && ['Must be less than max value']) || [];
  maxErrors.value = [];
}

function setDirtyMax(value: number): void {
  isDirty.value = true;
  dirtyMax.value = value;
  maxErrors.value = (dirtyMin.value && value < dirtyMin.value && ['Must be greater than min value']) || [];
  minErrors.value = [];
}

function clearSlider(): void {
  filterApplied.value = false;
  dirtyMin.value = minValue.value;
  dirtyMax.value = maxValue.value;
  emit('update:modelValue', []);
}

function formatRangeValue(val: string | number | undefined, numericValue: boolean): string {
  if (!val && val !== 0) return '';
  return numericValue ? filters.compactNumber(Number(val)) : filters.compactMoney(Number(val));
}

function resetState() {
  dirtyMin.value = IsNullOrUndefined(selectedMin.value) ? minValue.value : selectedMin.value;
  dirtyMax.value = IsNullOrUndefined(selectedMax.value) ? maxValue.value : selectedMax.value;
  if (dirtyMin.value !== selectedMin.value || dirtyMax.value !== selectedMax.value) {
    isDirty.value = true;
  }
}

onMounted(() => {
	resetState();
});

watch(() => props.values, () => {
	if (props.modelValue && props.modelValue.length > 0) {
		dirtyMin.value = props.modelValue[0];
		dirtyMax.value = props.modelValue[1];
		filterApplied.value = true;
	}

  resetState();
});

watch(() => props.modelValue, () => {
  if (props.modelValue && props.modelValue.length == 0) {
    dirtyMin.value = undefined;
    dirtyMin.value = undefined;
    filterApplied.value = false;
  }
});

</script>

<template>
  <FilterButton :title="buttonTitle" :active="filterApplied" :data-testid="`${dataTestPrefix}-filter-button`">
      <template #menu="{ close }">
        <div class="menu">
          <h4>{{title}}</h4>
          <h5>{{description}}</h5>
          <div class="control">
            <div class="input-row" v-show="!useSlider">
              <NumberInput 
                :label="minTitle"
                :data-testid="`${dataTestPrefix}-filter-min`"
                :data-parent-test='`${dataTestPrefix}-min-filter-div`'
                :model-value="dirtyMin"
                @update:model-value="(val) => {val && setDirtyMin(Number(val))}"
                outlined 
                persistent-hint 
                :hint="format(dirtyMin)"
                :error-messages="minErrors"
                :prefix="prefix"
              />
              <NumberInput 
                :label="maxTitle"
                :data-testid="`${dataTestPrefix}-filter-max`"
                :data-parent-test='`${dataTestPrefix}-max-filter-div`'
                :model-value="dirtyMax === 0 ? '' : dirtyMax"
                @update:model-value="(val) => {val && setDirtyMax(Number(val))}"
                outlined 
                persistent-hint 
                :hint="format(dirtyMax)"
                :error-messages="maxErrors"
                :prefix="prefix"
              />
            </div>
            <div class="slider" v-show="useSlider">
              <v-range-slider :model-value="[dirtyMin || 0, dirtyMax || 0]" @update:modelValue="setRange" :max="maxValue" :min="minValue" color="primary" strict
                            :step="step" :data-testid="`${dataTestPrefix}-filter-slider`" hide-details class="align-center" :thumb-label="false">
              </v-range-slider>
              <div class="display">
                <div class="values">
                  <div class="min">{{ formattedMin }}</div>
                  <div class="sep">&ndash;</div>
                  <div class="max">{{ formattedMax }}</div>
                </div>
              </div>
            </div>
            <template v-if="!hideSlider">
              <v-btn color="primary" block variant="text" @click="useSlider = !useSlider" :data-testid="`${dataTestPrefix}-filter-slider-toggle`">
                {{ useSlider ? 'Enter min/max values' : 'use slider' }}
              </v-btn>
            </template>
          </div>
          <div class="buttons">
            <v-btn size="small" variant="outlined" @click="clearSlider" :data-testid="`${dataTestPrefix}-filter-clear`">Clear</v-btn>
            <v-btn size="small" variant="flat" color="primary" :disabled="(minErrors.length + maxErrors.length) > 0" @click="() => {applyNewRange(); close();}" :data-testid="`${dataTestPrefix}-filter-apply`">Apply</v-btn>
          </div>
        </div>
      </template>
    </FilterButton>
</template>

<style scoped>
.menu {
  padding: 1rem 0 0 0;
  min-width: 400px;
  max-width: 500px;
}

.menu > h5 {
  font-weight: normal;
}

.menu > h4,
.menu > h5 {
  padding: 0 1rem;
}

.menu > .control {
  padding: 1rem;
  text-align: center;
}

.menu > .buttons {
  padding: 1rem;
  border-top: 1px solid var(--v-border-base);
  background: var(--v-gray-lighten5);
  display: flex;
  justify-content: space-between;
}

.slider {
  margin: 2.5em 1.5em 0 1.5em;
}

.input-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.values {   
  margin: 0 1rem 1rem 1rem;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: auto auto auto;
  grid-column-gap: 0.5rem;
  justify-content: center;
  align-content: center;
  align-items: center;
}

.values {
  font-size: 1.3em;
  font-weight: bold;
}

.values > .sep {
  text-align: center;
  font-weight: 300;
  font-size: 1rem;
}
</style>
